// NEEDS YOU, the time rail and the map insets (docs/design/inbox.md §8,
// R1846): the group of the INBOX that holds each message with an open
// action, in the form of the Impeccable comp C.
//
// **THE SHAPE.** The comp hangs each item off a time rail by a numbered
// circle and writes its body as prose. The owner's shape rule wins over the
// prose: an item is line 1 and line 2 of the row grammar (`rows.ts`), one
// row of presses and a map inset at the right, so one type of message has
// one shape and no name wraps a line.
//
// **THE RAIL.** A hairline at the left. An hour tick with `HH:00` stands
// where the hour of an item changes, and a day break (TODAY, YESTERDAY or
// the date) where its day changes, on the reader's wall clock. An item with
// a cell hangs by a circle with its number, and the inbox pins
// (`fast/chart/pieces/inbox-pins.ts`) stand the same number on the map; an
// item with no cell hangs by a small mark with no number.
//
// **NO TICKING.** The block writes no countdown of its own, and the rail
// moves only on a new list, at a new hour or day and at the end of a siege
// window, so the 4 Hz compare of the panel finds the same markup between
// two of those (audit P11).
//
// **THE FOLD.** The head `NEEDS YOU · n` folds the group into its one line
// with ▴ and opens it with ▾, as the SINCE YOU LEFT line does. The fold
// holds for the life of the page (`needs.ts`), and the pins leave the map
// while the group is folded.

import './needsyou.css';
import { CHART } from '../../gen/presentation';
import { inboxNeeds as S } from '../../gen/strings';
import { isoDate, pad2 } from '../../locale/stamp';
import type { GameState } from '../../state';
import { arrowFace } from '../arrowpress';
import { esc } from '../esc';
import { insetSvg, placeOf } from './inset';
import {
  needsBad, needsFolded, needsOf, needsPressed, setNeedsFocus, setNeedsFolded, setNeedsHover,
  setNeedsPressed, type NeedsItem,
} from './needs';
import type { InboxCtx } from './page';
import { onInboxPress } from './press';
import { lineOneHtml, lineTwoHtml, pressesHtml } from './rows';

/** The press of the head. */
export const FOLD_ACT = 'ib-n-fold';

/** The press that opens an item: the record's open page (lane R). */
export const OPEN_ACT = 'ib-r-open';

/** One row of the rail: a day break, or an item with its hour tick. */
export type RailRow =
  | { kind: 'day'; word: string }
  | { kind: 'item'; item: NeedsItem; hour: string };

/** The local day of a wall second, and the day before it, as dates. */
function dayOf(unixSec: number, back = 0): string {
  const d = new Date(unixSec * 1000);
  if (back !== 0) d.setDate(d.getDate() - back);
  return isoDate(d.getTime() / 1000);
}

/** The word of a day break: TODAY, YESTERDAY, or the date. */
export function dayWord(unixSec: number, nowSec: number): string {
  const day = dayOf(unixSec);
  if (day === dayOf(nowSec)) return S.today;
  if (day === dayOf(nowSec, 1)) return S.yesterday;
  return day;
}

/**
 * The rows of the rail, newest first: a day break before the first item
 * of each day, and an hour tick on the first item of each hour of that
 * day. Both read the reader's wall clock, as every stamp does (R1544).
 */
export function railRows(items: readonly NeedsItem[], nowSec: number): RailRow[] {
  const out: RailRow[] = [];
  let lastDay = '';
  let lastHour = -1;
  for (const item of items) {
    const unix = item.msg.unix;
    const day = dayOf(unix);
    if (day !== lastDay) {
      out.push({ kind: 'day', word: dayWord(unix, nowSec) });
      lastDay = day;
      lastHour = -1;
    }
    const hour = new Date(unix * 1000).getHours();
    out.push({ kind: 'item', item, hour: hour !== lastHour ? `${pad2(hour)}:00` : '' });
    lastHour = hour;
  }
  return out;
}

/** The circle of an item with a cell, or the mark of an item with none. */
function railMark(it: NeedsItem): string {
  if (it.num === 0) return `<span class="ny-m" role="img" aria-label="${esc(S.no_pin)}"></span>`;
  return `<span class="ny-n" role="img" aria-label="${esc(S.pin_label({ num: it.num }))}">${it.num}</span>`;
}

/** One item: its rail column, then the lines and the inset as ONE press
 *  that opens it, then the presses of the message. */
function itemHtml(it: NeedsItem, hour: string, s: GameState): string {
  const m = it.msg;
  const px = CHART.pieces.inbox_pins.fig.inset_px;
  const inset = it.num > 0 ? insetSvg(s.hello?.blocked ?? null, m.cell, it.num, placeOf(m, s)) : '';
  const acts = pressesHtml(m, s);
  const cls = `ny-e${needsBad(m) ? ' ny-bad' : ''}${s.inboxIsRead(m) ? '' : ' ny-new'}`
    + (m.id === needsPressed() ? ' ny-lit' : '');
  return `<li class="${cls}" data-msg="${m.id}">`
    + `<div class="ny-r">${hour ? `<span class="ny-t">${hour}</span>` : ''}${railMark(it)}</div>`
    + '<div class="ny-i">'
    + `<button class="ny-open" data-act="${OPEN_ACT}" data-id="${m.id}">`
    + `<span class="ny-ln">${lineOneHtml(m, s)}${lineTwoHtml(m, s)}</span>`
    + `<span class="ny-ins" style="width:${px}px;height:${px}px">${inset}</span>`
    + '</button>'
    + (acts ? `<div class="ny-acts">${acts}</div>` : '')
    + '</div></li>';
}

/** The head line: NEEDS YOU · n, a hairline and the fold mark. */
function headHtml(count: number, folded: boolean): string {
  return `<button class="ny-head" data-act="${FOLD_ACT}" aria-expanded="${!folded}"`
    + ` title="${esc(folded ? S.unfold_title : S.fold_title)}">`
    + `<span class="ny-hw">${esc(S.head({ count }))}</span>`
    + '<span class="ny-rule" aria-hidden="true"></span>'
    + `<span class="ny-chev" aria-hidden="true">${arrowFace(folded ? 'down' : 'up')}</span>`
    + '</button>';
}

/** The whole group, or '' while no message needs the reader. */
export function needsYouHtml(c: InboxCtx, nowSec = Date.now() / 1000): string {
  const list = needsOf(c.state.inbox, nowSec);
  if (list.items.length === 0) return '';
  const folded = needsFolded();
  let body = '';
  if (!folded) {
    for (const row of railRows(list.items, nowSec)) {
      body += row.kind === 'day'
        ? `<li class="ny-day">${esc(row.word)}</li>`
        : itemHtml(row.item, row.hour, c.state);
    }
    body = `<ol class="ny-rail">${body}</ol>`;
  }
  return `<section class="ny${folded ? ' folded' : ''}" aria-label="${esc(S.group_label)}">`
    + headHtml(list.items.length, folded) + body + '</section>';
}

// The head folds and opens the group; the overlay draws the panel again
// after the press (`press.ts`), and the chart reads the fold on its next
// frame, so the pins leave or come back with the group.
onInboxPress('ib-n-', (act) => {
  if (act === FOLD_ACT) setNeedsFolded(!needsFolded());
});

/** The message id of the NEEDS YOU item an event target stands in, or 0. */
function itemOf(t: EventTarget | null): number {
  const el = t instanceof Element ? t.closest<HTMLElement>('.ny-e[data-msg]') : null;
  return el ? Number(el.dataset.msg) : 0;
}

/** The documents whose light is armed. */
const armed = new WeakSet<Document>();

/**
 * Lights the pin of an item (comp C's focal point, inbox.md §8): the item
 * a mouse points at and the item that holds the keyboard focus light their
 * pin while they hold, and a press on an item keeps its pin lit while the
 * INBOX stands, because a telephone has no hover. The listeners stand on
 * the document in the capture phase, so they read the item before the
 * overlay acts on the press and redraws the panel. They write the light
 * alone: the markup does not change on a hover, so no panel is rebuilt
 * under the pointer, and the chart reads the light on its next frame.
 */
function armNeedsLight(doc: Document): void {
  if (armed.has(doc)) return;
  armed.add(doc);
  doc.addEventListener('pointerover', (ev) => {
    if (ev.pointerType === 'mouse') setNeedsHover(itemOf(ev.target));
  }, true);
  doc.addEventListener('pointerout', (ev) => {
    if (ev.pointerType === 'mouse' && ev.relatedTarget === null) setNeedsHover(0);
  }, true);
  doc.addEventListener('focusin', (ev) => { setNeedsFocus(itemOf(ev.target)); }, true);
  doc.addEventListener('focusout', (ev) => { setNeedsFocus(itemOf(ev.relatedTarget)); }, true);
  doc.addEventListener('pointerup', (ev) => {
    const id = itemOf(ev.target);
    if (id) setNeedsPressed(id);
  }, true);
  doc.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const id = itemOf(ev.target);
    if (id) setNeedsPressed(id);
  }, true);
}

if (typeof document !== 'undefined') armNeedsLight(document);
